import { ReactNode, useEffect, useMemo, useState } from "react";
import useGridRowSelection from "hooks/useGridRowSelection";
import { InputAdornment, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import InfoIcon from "assets/icons/info_icon.svg?react";
import CustomToolbar from "components/CaseDossier/Toolbar";
import SearchIcon from "@mui/icons-material/Search";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock, faCheck, faXmark, faBan, faHourglass,
  faCircleCheck, faCircleXmark, faTriangleExclamation,
  faPlay, faPause, faForwardStep, faFlag, faQuestion,
  faArrowRight, faStar, faLock,
  faChevronDown, faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
  GridRowGroupingModel,
} from "@mui/x-data-grid-premium";
import budgetService, { DEFAULT_BUDGET_YEAR } from "api/budgetService";
import statusService from "api/statusService";
import uiSettingsService from "api/uiSettingsService";
import BudgetYearSelect from "components/Budget/BudgetYearSelect";
import EngagementMetricSlide from "components/Budget/EngagementMetricSlide";
import BudgetEngagementDetailModal from "components/Budget/BudgetEngagementDetailModal";
import BudgetEngagementSettlementsModal from "components/Budget/BudgetEngagementSettlementsModal";
import CreateEngagementCorrectionModal from "components/Budget/CreateEngagementCorrectionModal";
import ConfirmModal from "components/ConfirmModal";
import StatusBadge, { getStatusColor } from "components/StatusBadge";
import { IBudgetEngagementListItem } from "dto/Budget/IBudgetEngagement";
import { IStatus, StatusType } from "dto/IStatus";
import { ListType } from "dto/ListType";
import { TagType } from "dto/ITag";
import { IGridFilter } from "dto/IGridFilter";
import { formatMoney } from "utils/formatMoney";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { plGridLocales } from "utils/muiGridLocales";
import { ENotificationType, notification } from "utils/notification";
import MenuCell from "./Cells/MenuCell";
import styles from "./styles.module.scss";

type QuickFilter = "all" | "to_accept" | "accepted" | "to_settle" | "settled";

const QUICK_FILTER_ORDER: QuickFilter[] = [
  "all",
  "to_accept",
  "accepted",
  "to_settle",
  "settled",
];

const QUICK_FILTER_LABELS: Record<QuickFilter, string> = {
  all: "Wszystkie",
  to_accept: "Do akceptacji",
  accepted: "Zaakceptowane",
  to_settle: "Do rozliczenia",
  settled: "Rozliczone",
};

interface ApprovalEvent {
  who: string;
  when: string;
  action: "submit" | "approve" | "reject" | "send_back";
}

type EngagementRow = IBudgetEngagementListItem & {
  approvalTimeline?: ApprovalEvent[];
  /** Symbol działu klasyfikacji budżetowej (np. "750"). */
  DivisionSymbol?: string;
  /** Nazwa działu (np. "Administracja publiczna"). */
  DivisionName?: string;
  /** Nazwa rozdziału (symbol jest w ChapterSymbol). */
  ChapterName?: string;
};

/** Dane działu/rozdziału powiązanego z zaangażowaniem (numer + tytuł, do tooltipa i metryki). */
const getClassification = (row: EngagementRow) => {
  const chapterSymbol = row.ChapterSymbol ?? "";
  const divisionSymbol = row.DivisionSymbol ?? chapterSymbol.slice(0, 3);
  return {
    divisionSymbol,
    divisionName: row.DivisionName ?? "",
    chapterSymbol,
    chapterName: row.ChapterName ?? "",
  };
};

// Jasny styl tooltipa (jak timeline w kolumnie Status) — tło, ramka, czcionka.
const LIGHT_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: "#fdf8fd",
      color: "#1c1b1f",
      fontSize: 13,
      fontWeight: 400,
      lineHeight: 1.5,
      border: "1px solid #c9c5d0",
      borderRadius: "8px",
      boxShadow: "0px 4px 8px 3px rgba(0,0,0,0.15)",
      maxWidth: 320,
      p: "12px 16px",
      "& .MuiTooltip-arrow": {
        color: "#fdf8fd",
        "&::before": { border: "1px solid #c9c5d0" },
      },
    },
  },
};

const ACTION_LABELS: Record<string, string> = {
  submit: "Złożono wniosek",
  approve: "Zaakceptowano",
  reject: "Odrzucono",
  send_back: "Odesłano do poprawy",
};

const fmtDateTime = (iso: string) => {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
};

// Timeline zsynchronizowany ze stylem listy Przychodzące (MetricDatesModal):
// jasne tło, szare kropki + linia, opis ciemny, data szara.
const renderTimeline = (timeline: ApprovalEvent[]) => (
  <div className={styles.tlList}>
    {timeline.map((ev, i) => (
      <div className={styles.tlBox} key={i}>
        <div className={styles.tlBoxWrapper}>
          <div className={styles.tlLine} />
          <div className={styles.tlDot} />
          <div className={styles.tlContent}>
            <div className={styles.tlR1}>
              {ACTION_LABELS[ev.action] ?? ev.action}
            </div>
            <div className={styles.tlR2}>
              <span className={styles.tlWho}>{ev.who}</span>
              <span className={styles.tlDate}>{fmtDateTime(ev.when)}</span>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ICON_COLLECTION: { name: string; icon: IconDefinition }[] = [
  { name: "clock",                icon: faClock                },
  { name: "hourglass",            icon: faHourglass            },
  { name: "play",                 icon: faPlay                 },
  { name: "pause",                icon: faPause                },
  { name: "forward-step",         icon: faForwardStep          },
  { name: "check",                icon: faCheck                },
  { name: "circle-check",         icon: faCircleCheck          },
  { name: "xmark",                icon: faXmark                },
  { name: "circle-xmark",         icon: faCircleXmark          },
  { name: "ban",                  icon: faBan                  },
  { name: "triangle-exclamation", icon: faTriangleExclamation  },
  { name: "flag",                 icon: faFlag                 },
  { name: "question",             icon: faQuestion             },
  { name: "arrow-right",          icon: faArrowRight           },
  { name: "star",                 icon: faStar                 },
  { name: "lock",                 icon: faLock                 },
];

const iconByName = (name: string | undefined): IconDefinition | null =>
  ICON_COLLECTION.find((i) => i.name === name)?.icon ?? null;

const DEMO_ENGAGEMENT_STATUSES: IStatus[] = [
  { Id: -701, Name: "Do akceptacji", Color: 4 },
  { Id: -702, Name: "Zaakceptowane", Color: 6 },
  { Id: -703, Name: "Do poprawy", Color: 11 },
  { Id: -704, Name: "Rozliczone", Color: 3 },
  { Id: -705, Name: "Anulowane", Color: 7 },
];

const EngagementsRegister = () => {
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();
  const { t } = useTranslation();
  const [year, setYear] = useState(DEFAULT_BUDGET_YEAR);
  const [search, setSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [showQuickModal, setShowQuickModal] = useState(false);
  const [rowGroupingModel, setRowGroupingModel] =
    useState<GridRowGroupingModel>([]);

  // Pasek funkcji (CustomToolbar — taki jak na liście Rejestr / Teczki)
  const [editFilter, setEditFilter] = useState<IGridFilter>(null);
  const [filterStatusId, setFilterStatusId] = useState<number>(null);
  const [filterTagId, setFilterTagId] = useState<string>(null);
  const [filterUserId, setFilterUserId] = useState<string>(null);

  const [detail, setDetail] = useState<IBudgetEngagementListItem | null>(null);
  const [settlementsOf, setSettlementsOf] =
    useState<IBudgetEngagementListItem | null>(null);
  const [correctionOf, setCorrectionOf] =
    useState<IBudgetEngagementListItem | null>(null);
  const [deleting, setDeleting] = useState<IBudgetEngagementListItem | null>(
    null,
  );
  const [metricOf, setMetricOf] = useState<EngagementRow | null>(null);

  // Jedna firma na użytkownika, bez przełącznika kontekstu (decyzja D1) — nie ma już
  // zdarzenia "zmiana firmy" w trakcie sesji, więc czyszczenie modali po takiej zmianie
  // jest zbędne (dawne useOnProfileChange nigdy by się tu nie odpaliło).

  const workspaceId = getWorkspaceId();
  const { data: engagements = [], isLoading } =
    budgetService.useGetAllEngagements(year);
  const { mutate: deleteEngagement } = budgetService.useDeleteEngagement();
  const { mutate: submitEngagement } = budgetService.useSubmitEngagement();

  const handleSubmit = (row: IBudgetEngagementListItem) => {
    submitEngagement(row.Id, {
      onSuccess: (e) => {
        notification({
          type: ENotificationType.SUCCESS,
          title: "Wysłano",
          text:
            e.Status === "Zaakceptowany"
              ? "Brak aktywnej procedury — zaangażowanie zaakceptowane od razu."
              : "Zaangażowanie trafiło do procedury akceptacji.",
        });
      },
      onError: (err: unknown) => {
        const msg =
          (err as { response?: { data?: { errorCodes?: { message: string }[] } } })
            ?.response?.data?.errorCodes?.[0]?.message ??
          "Nie udało się wysłać do akceptacji.";
        notification({ type: ENotificationType.ERROR, title: "Błąd", text: msg });
      },
    });
  };
  const { data: systemStatusesData } = statusService.useGetStatuses(
    workspaceId!,
    StatusType.ENGAGEMENTS,
  );

  // Konfiguracja wyświetlania statusów z BE → hydratacja warstwy podręcznej
  // (localStorage), z której synchronicznie czyta renderCell kolumny Status.
  const { data: beStatusConfig } =
    uiSettingsService.useGetStatusDisplay("ENGAGEMENTS");

  const engagementStatuses: IStatus[] = useMemo(() => {
    const raw = systemStatusesData?.data ?? [];
    const base = raw.length > 0 ? raw : DEMO_ENGAGEMENT_STATUSES;
    const overrides: Record<number, { Name: string; Color: number }> = {};
    try {
      Object.assign(
        overrides,
        JSON.parse(localStorage.getItem("status_chip_overrides_v1") || "{}"),
      );
    } catch {
      /* localStorage niedostępny — używamy tylko danych BE */
    }
    // BE jest źródłem prawdy — nadpisania z backendu mają pierwszeństwo.
    for (const o of beStatusConfig?.Overrides ?? []) {
      overrides[o.StatusId] = { Name: o.Name, Color: o.Color };
    }
    return base.map((s) => {
      const ov = overrides[s.Id as number];
      return ov ? { ...s, Name: ov.Name, Color: ov.Color } : s;
    });
  }, [systemStatusesData, beStatusConfig]);

  useEffect(() => {
    if (!beStatusConfig) return;
    try {
      const modeMap = JSON.parse(
        localStorage.getItem("status_display_mode_v1") || "{}",
      );
      modeMap["ENGAGEMENTS"] = beStatusConfig.Mode === "icon" ? "icon" : "chip";
      localStorage.setItem("status_display_mode_v1", JSON.stringify(modeMap));

      const iconMap = JSON.parse(
        localStorage.getItem("status_icons_v1") || "{}",
      );
      for (const i of beStatusConfig.Icons ?? [])
        iconMap[i.StatusId] = i.IconName;
      localStorage.setItem("status_icons_v1", JSON.stringify(iconMap));

      const overrides = JSON.parse(
        localStorage.getItem("status_chip_overrides_v1") || "{}",
      );
      for (const o of beStatusConfig.Overrides ?? [])
        overrides[o.StatusId] = { Name: o.Name, Color: o.Color };
      localStorage.setItem(
        "status_chip_overrides_v1",
        JSON.stringify(overrides),
      );
    } catch {
      /* localStorage niedostępny — pomijamy hydratację cache */
    }
  }, [beStatusConfig]);

  const allEngagements: EngagementRow[] = useMemo(
    () => engagements as EngagementRow[],
    [engagements],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const statusName =
      filterStatusId != null
        ? engagementStatuses
            .find((s) => s.Id === filterStatusId)
            ?.Name.toLowerCase()
        : null;
    return allEngagements.filter((e) => {
      if (quickFilter === "to_accept" && e.Accepted) return false;
      if (quickFilter === "accepted" && !e.Accepted) return false;
      if (quickFilter === "to_settle" && e.SettledAmount > 0) return false;
      if (quickFilter === "settled" && e.SettledAmount <= 0) return false;
      if (statusName && (e.Status ?? "").toLowerCase() !== statusName)
        return false;
      if (!q) return true;
      return [
        e.Title,
        e.Signature,
        e.ExpenseTitle,
        e.ChapterSymbol,
        e.Status,
      ].some((f) => (f ?? "").toLowerCase().includes(q));
    });
  }, [allEngagements, search, quickFilter, filterStatusId, engagementStatuses]);

  const columns: GridColDef[] = [
    {
      field: "Signature",
      headerName: "Sygnatura",
      width: 140,
      renderCell: (params: GridRenderCellParams<IBudgetEngagementListItem>) =>
        params.row?.Signature ? (
          <span className={styles.signature}>{params.row.Signature}</span>
        ) : null,
    },
    {
      field: "Title",
      headerName: "Tytuł zaangażowania",
      flex: 1,
      minWidth: 260,
    },
    {
      field: "ExpenseTitle",
      headerName: "Wydatek",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "ChapterSymbol",
      headerName: "Rozdział",
      width: 100,
      renderCell: (params: GridRenderCellParams<EngagementRow>) => {
        const row = params.row as EngagementRow;
        if (!row?.ChapterSymbol) return null;
        const c = getClassification(row);
        return (
          <Tooltip
            placement="left"
            componentsProps={{
              tooltip: {
                sx: { ...LIGHT_TOOLTIP_PROPS.tooltip.sx, maxWidth: 480 },
              },
            }}
            title={
              <>
                <div>
                  <b>Dział {c.divisionSymbol}</b>
                  {c.divisionName ? ` — ${c.divisionName}` : ""}
                </div>
                <div>
                  <b>Rozdział {c.chapterSymbol}</b>
                  {c.chapterName ? ` — ${c.chapterName}` : ""}
                </div>
              </>
            }
          >
            <span style={{ cursor: "default" }}>{row.ChapterSymbol}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "Status",
      headerName: "Status",
      width: 160,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<EngagementRow>) => {
        const row = params.row as EngagementRow;
        const label: string = row?.Status ?? "";
        const timeline = row?.approvalTimeline;

        // Read fresh from localStorage inside renderCell to avoid DataGrid stale-closure issues
        const mode: "chip" | "icon" = (() => {
          try {
            return (JSON.parse(
              localStorage.getItem("status_display_mode_v1") || "{}",
            )["ENGAGEMENTS"] ?? "chip") as "chip" | "icon";
          } catch {
            return "chip";
          }
        })();

        const iconMapLS: Record<string, string> = (() => {
          try { return JSON.parse(localStorage.getItem("status_icons_v1") || "{}"); }
          catch { return {}; }
        })();

        const matched = engagementStatuses.find(
          (s) => s.Name.toLowerCase() === label.toLowerCase(),
        );

        const tooltipSx = LIGHT_TOOLTIP_PROPS;

        const cellStyle: React.CSSProperties = {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
        };

        let inner: ReactNode = null;
        if (matched) {
          if (mode === "icon") {
            const iconName: string | undefined = iconMapLS[String(matched.Id)];
            const faIcon = iconByName(iconName);
            const color = getStatusColor(matched.Color);
            inner = faIcon ? (
              <FontAwesomeIcon icon={faIcon} style={{ color, fontSize: 18 }} />
            ) : (
              <span
                title={matched.Name}
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: color,
                  flexShrink: 0,
                }}
              />
            );
          } else {
            inner = <StatusBadge status={matched} showName small />;
          }
        } else if (label) {
          inner = (
            <span style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
              {label}
            </span>
          );
        }

        if (!inner) return null;

        if (timeline?.length) {
          return (
            <Tooltip
              title={renderTimeline(timeline)}
              placement="left"
              arrow
              componentsProps={tooltipSx}
            >
              <span style={{ ...cellStyle, cursor: "pointer" }}>{inner}</span>
            </Tooltip>
          );
        }

        if (mode === "icon" && matched) {
          return (
            <Tooltip
              title={matched.Name}
              placement="left"
              arrow
              componentsProps={tooltipSx}
            >
              <span style={cellStyle}>{inner}</span>
            </Tooltip>
          );
        }

        return <span style={cellStyle}>{inner}</span>;
      },
    },
    {
      field: "Amount",
      headerName: "Zaangażowane",
      width: 150,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => formatMoney(value as number),
    },
    {
      field: "SettledAmount",
      headerName: "Rozliczone",
      width: 140,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => formatMoney(value as number),
    },
    {
      // Kolumna „Met." — ujednolicona ze wzorcem rejestru Teczek/Umów.
      field: "metric",
      headerName: "Met.",
      width: 70,
      sortable: false,
      filterable: false,
      groupable: false,
      hideable: false,
      disableColumnMenu: true,
      disableReorder: true,
      align: "center",
      headerAlign: "center",
      renderHeader: () => <div className={styles.header}>Met.</div>,
      renderCell: (params: GridRenderCellParams<EngagementRow>) => {
        if (params.rowNode.type === "group") return <></>;
        const row = params.row;
        if (!row) return null;
        return (
          <Tooltip title={t("cases.metric")} placement="left">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                borderRadius: 4,
                cursor: "pointer",
              }}
              onClick={() => setMetricOf(row)}
            >
              <InfoIcon style={{ width: 18, height: 18 }} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      align: "center",
      renderCell: (params: GridRenderCellParams<IBudgetEngagementListItem>) => {
        const row = params.row;
        if (!row) return null;
        return (
          <MenuCell
            row={row}
            onDetail={setDetail}
            onSettlements={setSettlementsOf}
            onCorrection={setCorrectionOf}
            onDelete={setDeleting}
            onSubmit={handleSubmit}
          />
        );
      },
    },
  ];

  const EngagementsToolbar = () => (
    <CustomToolbar
      listName="engagementsRegister"
      editFilter={editFilter}
      setEditFilter={setEditFilter}
      listType={ListType.Engagements}
      statusType={StatusType.ENGAGEMENTS}
      statusId={filterStatusId}
      setStatusId={setFilterStatusId}
      tagType={TagType.Other}
      tagId={filterTagId}
      setTagId={setFilterTagId}
      userId={filterUserId}
      setUserId={setFilterUserId}
      dateFilterLabel="Data zaangażowania"
      filterAsSlide
      rightChildren={
        <div className={styles.quickType}>
          <div
            className={styles.quickTypeText}
            onClick={() => setShowQuickModal((v) => !v)}
          >
            {QUICK_FILTER_LABELS[quickFilter]}
            <FontAwesomeIcon
              icon={showQuickModal ? faChevronUp : faChevronDown}
            />
          </div>
          {showQuickModal && (
            <div className={styles.quickDropdown}>
              {QUICK_FILTER_ORDER.map((k) => (
                <div
                  key={k}
                  className={`${styles.quickBox} ${
                    quickFilter === k ? styles.quickBoxActive : ""
                  }`}
                  onClick={() => {
                    setQuickFilter(k);
                    setShowQuickModal(false);
                  }}
                >
                  {QUICK_FILTER_LABELS[k]}
                </div>
              ))}
            </div>
          )}
        </div>
      }
    />
  );

  const gridRows = rows.map((e) => ({ ...e, id: e.Id }));
  rowsRef.current = gridRows;

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.title}>Zaangażowania</span>
          <BudgetYearSelect
            value={year}
            onChange={setYear}
            className={styles.yearSelect}
          />
        </div>

        <div className={styles.headerRight}>
          <TextField
            size="small"
            placeholder="Szukaj na liście"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchBox}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </div>
      </div>

      <div className={styles.grid}>
        <DataGridPremium
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
          }}
          localeText={plGridLocales}
          loading={isLoading}
          columnHeaderHeight={36}
          columns={columns}
          rows={gridRows}
          rowGroupingModel={rowGroupingModel}
          onRowGroupingModelChange={setRowGroupingModel}
          slots={{ toolbar: EngagementsToolbar }}
          showToolbar
          hideFooter
          disableAggregation
          disableColumnFilter
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
        />
      </div>

      {detail && (
        <BudgetEngagementDetailModal
          engagement={detail}
          onClose={() => setDetail(null)}
        />
      )}

      {settlementsOf && (
        <BudgetEngagementSettlementsModal
          engagement={settlementsOf}
          onClose={() => setSettlementsOf(null)}
        />
      )}

      {correctionOf && (
        <CreateEngagementCorrectionModal
          engagement={correctionOf}
          onClose={() => setCorrectionOf(null)}
        />
      )}

      {deleting && (
        <ConfirmModal
          title="Usunąć zaangażowanie?"
          text={`Czy na pewno usunąć zaangażowanie „${
            deleting.Title || deleting.Signature || ""
          }"?`}
          acceptLabel="Usuń"
          cancelLabel="Anuluj"
          showDontShowNextTime={false}
          onAccept={() =>
            deleteEngagement(deleting.Id, {
              onSuccess: () => setDeleting(null),
              onError: () => {
                notification({
                  title: "Nie udało się usunąć",
                  text: "Zaangażowania nie można usunąć w bieżącym stanie.",
                  type: ENotificationType.ERROR,
                });
                setDeleting(null);
              },
            })
          }
          onCancel={() => setDeleting(null)}
        />
      )}

      {metricOf && (
        <EngagementMetricSlide
          engagement={metricOf}
          onClose={() => setMetricOf(null)}
        />
      )}
    </div>
  );
};

export default EngagementsRegister;
